import { useState } from "react";
import { MoreVertical, Trash2 } from "lucide-react";
import type { CrmContactAction } from "@ease/crm/contact-action-groups";
import type { CrmContactSummarySignal } from "@ease/crm/contact-summary-signals";
import { CrmContactActionLink } from "@/components/crm/contact-action-groups-card";
import {
  CrmContactHeaderBadges,
  CrmHeaderHealthSparkline,
  type CrmContactHealthTrendPoint,
  type CrmContactMomentumDirection,
} from "@/components/crm/contact-header-indicators";
import { CrmContactGlanceSignals } from "@/components/crm/contact-glance-signals";

interface CrmContactHeaderSummaryProps {
  displayName: string;
  email: string | null;
  firstName?: string | null;
  lastName?: string | null;
  leadScore?: number | null;
  healthScore?: number | null;
  momentumDirection?: CrmContactMomentumDirection | null;
  actions: CrmContactAction[];
  signals: CrmContactSummarySignal[];
  healthHistory: CrmContactHealthTrendPoint[];
  onDeleteRequest: () => void;
}

export function CrmContactHeaderSummary({
  displayName,
  email,
  firstName,
  lastName,
  leadScore,
  healthScore,
  momentumDirection,
  actions,
  signals,
  healthHistory,
  onDeleteRequest,
}: CrmContactHeaderSummaryProps) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="flex items-center gap-3">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-primary-light)] text-lg font-semibold text-[var(--color-primary)]">
        {getCrmContactInitials({ displayName, firstName, lastName })}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">{displayName}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <CrmContactHeaderBadges
              leadScore={leadScore}
              healthScore={healthScore}
              momentumDirection={momentumDirection}
            />
          </div>
        </div>
        {email ? <p className="text-sm text-[var(--color-muted-foreground)]">{email}</p> : null}
        {actions.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2" aria-label="Contact quick actions">
            {actions.map((action) => (
              <CrmContactActionLink key={`header:${action.key}:${action.href}`} action={action} />
            ))}
          </div>
        ) : null}
        <CrmContactGlanceSignals signals={signals} />
        <div className="mt-3">
          <CrmHeaderHealthSparkline history={healthHistory} trendDirection={momentumDirection} />
        </div>
      </div>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowMenu((current) => !current)}
          onBlur={() => setTimeout(() => setShowMenu(false), 150)}
          className="rounded-lg p-1.5 text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]"
          aria-label="Open contact actions menu"
          aria-expanded={showMenu}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
        {showMenu ? (
          <div className="absolute right-0 top-full z-10 mt-1 w-40 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] py-1 shadow-lg">
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setShowMenu(false);
                onDeleteRequest();
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-[var(--color-destructive)] transition-colors hover:bg-[var(--color-destructive)]/5"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete Contact
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function getCrmContactInitials({
  displayName,
  firstName,
  lastName,
}: {
  displayName: string;
  firstName?: string | null;
  lastName?: string | null;
}) {
  const first = firstName?.trim()?.[0] ?? "";
  const last = lastName?.trim()?.[0] ?? "";
  const fallback = displayName.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("");
  return `${first}${last}`.trim().toUpperCase() || fallback.toUpperCase() || "?";
}
